import React, {FC} from 'react';
import {PricingFilters, PricingPaginationMeta} from '../pricing-grid/types';
import CatLoader from './product-filter-skeleton';
import {FilterMap} from 'features/table/slice/types';
import makeSelectEntityRequest from 'features/table/slice/selectors/make-select-entity-request';
import selectEntityMeta from 'features/table/slice/selectors/select-entity-meta';
import {useSelector} from 'react-redux';
import {Row, Col, Button} from 'reactstrap';
import {ObjectId} from 'types/object-id';
import {ProductCollection} from 'types/product-collection';
import {useGetPricingProductCollectionQuery} from 'features/commercial/quotations/store/endpoints/get-pricing-product-collections';

/**
 * Props for the Collections component.
 */
interface Props {
  /**
   * The current filters.
   */
  filters: FilterMap<PricingFilters>;
  /**
   * Callback function to handle filter changes.
   */
  onChangeFilters: (key: PricingFilters, value: any) => void;
}

/**
 * Component for displaying and managing product collections.
 *
 * @param props - The component props.
 * @returns The JSX elements for product collections.
 */
const Collections: FC<Props> = ({filters, onChangeFilters}) => {
  // Extract collection from filters and convert to ObjectId array
  const collectionQuery = filters['filter[product_collection_id]'] as
    | string
    | '';
  const collection: ObjectId[] = (collectionQuery?.split('|') || []).map(
    (id: ObjectId) => Number(id)
  );

  const {data, isLoading} = useGetPricingProductCollectionQuery(filters);

  const productCollections = data?.data?.product_collections ?? [];

  /**
   * Handles collection button click.
   *
   * @param c - The selected product collection.
   */
  const handleClick = (c: ProductCollection): void => {
    const found = collection.includes(c.id);

    if (found) {
      const updatedCollection = collection.filter(id => id !== c.id);
      onChangeFilters(
        'filter[product_collection_id]',
        updatedCollection.length === 0 ? null : updatedCollection.join('|')
      );
    } else {
      onChangeFilters(
        'filter[product_collection_id]',
        collection.concat(c.id).join('|')
      );
    }
  };

  return (
    <div>
      <Row className="align-items-center mb-3">
        <Col md={12}>
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-2">
              {isLoading ? (
                <CatLoader />
              ) : (
                productCollections.map(c => (
                  <Button
                    key={c.id}
                    color={'white'}
                    active={collection.includes(c.id)}
                    size="sm"
                    className={` border-1 border-bottom-0 font-size-14 text-center px-3 py-2 ${
                      collection.includes(c.id)
                        ? ' border-important text-important '
                        : ' border-gray-light'
                    } `}
                    onClick={() => handleClick(c)}
                  >
                    {c.name}{' '}
                    <span
                      className={`badge  ms-1 font-size-16 ${
                        collection.includes(c.id)
                          ? '  text-important border-important border-1 border '
                          : ' text-white bg-important '
                      } `}
                    >
                      {c.variant_count}
                    </span>
                  </Button>
                ))
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Collections;
